import { usePermissions } from '../../app/hooks/usePermissions';
import { getUserName } from '../../shared/utils/userAdapter';
import { 
  BookOpen, 
  PlayCircle,
  Award,
  Activity,
  TrendingUp
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  adminCursoService,
  adminLessonService
} from '../../shared/services';

export function InstructorDashboardPage() {
  const { user } = usePermissions();

  // Buscar cursos
  const { data: cursosData } = useQuery({
    queryKey: ['instructor-cursos-count'],
    queryFn: () => adminCursoService.getCursos({}),
  });

  // Buscar aulas
  const { data: lessonsData } = useQuery({
    queryKey: ['instructor-lessons-count'],
    queryFn: () => adminLessonService.getLessons({}),
  });

  const instructorCards = [
    {
      title: 'Meus Cursos',
      value: (cursosData as any[])?.length || 0,
      icon: BookOpen,
      color: 'blue',
      link: '/admin/cursos',
      description: 'Cursos que você ministra',
    },
    {
      title: 'Total de Aulas',
      value: (lessonsData as any[])?.length || 0,
      icon: PlayCircle,
      color: 'green',
      link: '/instructor/lessons',
      description: 'Aulas criadas por você',
    },
    {
      title: 'Aulas Ativas',
      value: (lessonsData as any[])?.filter((l: any) => l.isActive).length || 0,
      icon: Activity,
      color: 'purple',
      link: '/instructor/lessons',
      description: 'Aulas disponíveis para alunos',
    },
    {
      title: 'Cursos Ativos',
      value: (cursosData as any[])?.filter((c: any) => c.ativo).length || 0,
      icon: TrendingUp,
      color: 'orange',
      link: '/admin/cursos',
      description: 'Cursos publicados',
    },
  ];

  const quickActions = [
    {
      title: 'Nova Aula',
      description: 'Crie uma nova aula para seus cursos',
      icon: PlayCircle,
      link: '/instructor/lessons/new',
      color: 'blue',
    },
    {
      title: 'Gerenciar Aulas',
      description: 'Visualize e edite suas aulas',
      icon: BookOpen,
      link: '/instructor/lessons',
      color: 'green',
    },
    {
      title: 'Gerenciar Cursos',
      description: 'Organize seus cursos',
      icon: Award,
      link: '/admin/cursos',
      color: 'purple',
    },
  ];

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Award className="w-8 h-8 text-blue-600" />
            Painel do Instrutor
          </h1>
          <p className="text-gray-600 mt-2">
            Bem-vindo, {getUserName(user)}! Gerencie seus cursos e aulas.
          </p>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {instructorCards.map((card) => {
            const Icon = card.icon;
            const colorClasses = {
              blue: 'bg-blue-100 text-blue-600',
              green: 'bg-green-100 text-green-600',
              purple: 'bg-purple-100 text-purple-600',
              orange: 'bg-orange-100 text-orange-600',
            }[card.color];

            return (
              <Link
                key={card.title}
                to={card.link}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${colorClasses}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">
                  {card.title}
                </h3>
                <p className="text-3xl font-bold text-gray-900 mb-2">
                  {card.value}
                </p>
                <p className="text-xs text-gray-500">
                  {card.description}
                </p>
              </Link>
            );
          })}
        </div>

        {/* Ações Rápidas */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-600" />
            Ações Rápidas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {quickActions.map((action) => {
              const Icon = action.icon;
              const colorClasses = {
                blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
                green: 'bg-green-50 text-green-600 hover:bg-green-100',
                purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100',
              }[action.color];

              return (
                <Link
                  key={action.title}
                  to={action.link}
                  className={`${colorClasses} rounded-xl p-6 transition-colors border border-gray-200`}
                >
                  <Icon className="w-8 h-8 mb-3" />
                  <h3 className="text-lg font-bold mb-2">{action.title}</h3>
                  <p className="text-sm opacity-80">{action.description}</p>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Últimas Aulas */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <PlayCircle className="w-6 h-6 text-blue-600" />
              Últimas Aulas
            </h2>
            <Link
              to="/instructor/lessons"
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Ver todas →
            </Link>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {!lessonsData || lessonsData.length === 0 ? (
              <div className="p-12 text-center">
                <PlayCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-2">Nenhuma aula criada ainda</p>
                <p className="text-gray-400 text-sm mb-6">
                  Comece criando sua primeira aula
                </p>
                <Link
                  to="/instructor/lessons/new"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlayCircle className="w-5 h-5" />
                  Criar Primeira Aula
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {(lessonsData as any[])?.slice(0, 5).map((lesson: any) => (
                  <div key={lesson.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <PlayCircle className="w-5 h-5 text-blue-600" />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {lesson.title}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {lesson.description?.substring(0, 60)}...
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          lesson.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {lesson.isActive ? 'Ativa' : 'Inativa'}
                        </span>
                        <Link
                          to={`/instructor/lessons/${lesson.id}/edit`}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Editar
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
